import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from "../components/SEO";
import { PAGE_SEO } from "../seo-data";
import { ArrowRight, CheckCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const observerRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [categories, setCategories] = useState(["All"]);

  // Newsletter state
  const [nlEmail, setNlEmail] = useState('');
  const [nlStatus, setNlStatus] = useState(null);
  const [nlMsg, setNlMsg] = useState('');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const url = activeCategory === 'All'
          ? `${API}/api/blog/posts`
          : `${API}/api/blog/posts?category=${encodeURIComponent(activeCategory)}`;
        const res = await fetch(url);
        const data = await res.json();
        setPosts(data.posts || []);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to fetch posts:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [activeCategory]);

  // Fetch categories once
  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await fetch(`${API}/api/blog/categories`);
        const data = await res.json();
        const cats = Object.keys(data.categories || {});
        setCategories(["All", ...cats]);
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Failed to fetch categories:', e); }
    };
    fetchCats();
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observerRef.current.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });

    document.querySelectorAll('.reveal:not(.visible)').forEach(el => {
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [posts]);

  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    if (!nlEmail.trim()) return;
    setNlStatus('loading');
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: nlEmail.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setNlStatus('success');
        setNlMsg(data.message);
        setNlEmail('');
        setTimeout(() => setNlStatus(null), 5000);
      } else {
        setNlStatus('error');
        setNlMsg(data.detail || 'Something went wrong');
      }
    } catch {
      setNlStatus('error');
      setNlMsg('Network error. Please try again.');
    }
  };

  return (
    <div className="blog-page">
      <SEO {...PAGE_SEO.blog} />
      {/* Hero */}
      <section className="blog-hero">
        <div className="container">
          <div className="blog-hero-content reveal">
            <div className="eyebrow">Insights & Resources</div>
            <h1>The Skye AI <span className="gradient-text">Blog</span></h1>
            <p className="blog-hero-sub">
              Expert insights on AI-powered sales, revenue automation, and scaling your business without scaling your team.
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="blog-categories">
        <div className="container">
          <div className="categories-row reveal">
            {categories.map((cat, index) => (
              <button
                key={index}
                className={`category-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => { setActiveCategory(cat); setLoading(true); }}
                data-testid={`blog-category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="blog-posts-section">
        <div className="container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--mid)' }}>
              <Loader2 size={28} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : posts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--mid)' }}>
              <p>No posts found in this category.</p>
            </div>
          ) : (
            <div className="blog-posts-grid">
              {posts.map((post, index) => (
                <article
                  key={post.slug}
                  className="blog-card reveal"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`blog-card-${post.slug}`}
                >
                  <div className={`blog-card-thumbnail ${post.thumbnail}`}>
                    <span className={`blog-category-tag tag-${post.categoryColor}`}>
                      {post.category}
                    </span>
                  </div>
                  <div className="blog-card-content">
                    <h3>{post.title}</h3>
                    <p>{post.description}</p>
                    <div className="blog-card-meta">
                      <span className="meta-date">{post.date}</span>
                      <span className="meta-dot">·</span>
                      <span className="meta-time">{post.readTime}</span>
                    </div>
                    <Link to={`/blog/${post.slug}`} className="blog-read-more">
                      Read Article <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="blog-newsletter">
        <div className="container">
          <div className="newsletter-card reveal">
            <div className="newsletter-content">
              <h3>Get insights delivered</h3>
              <p>Join revenue leaders getting monthly tips on AI-powered sales.</p>
            </div>
            {nlStatus === 'success' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#10B981', fontWeight: 500 }}>
                <CheckCircle size={18} /> {nlMsg}
              </div>
            ) : (
              <form className="newsletter-form" onSubmit={handleNewsletterSubscribe}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={nlEmail}
                  onChange={e => setNlEmail(e.target.value)}
                  required
                  data-testid="blog-newsletter-input"
                />
                <button className="btn-gradient" type="submit" disabled={nlStatus === 'loading'} data-testid="blog-newsletter-submit">
                  {nlStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
            )}
            {nlStatus === 'error' && (
              <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{nlMsg}</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
